import { useEffect, useState } from 'react'
import { useAudio } from './hooks/useAudio'
import { useWebSocket } from './hooks/useWebSocket'
import { useGameStore } from './store/gameStore'
import { MenuScreen } from './components/lobby/MenuScreen'
import { WaitingRoom } from './components/lobby/WaitingRoom'
import { GameShell } from './components/game/GameShell'
import { MobileGameShell } from './components/mobile/MobileGameShell'
import { MenuAudioToggles } from './components/shared/AudioControls'
import { assetUrl, cssUrl } from './utils/assets'
import './styles/main.css'

function ErrorToast() {
  const { errorMessage, setError } = useGameStore()
  useEffect(() => {
    if (!errorMessage) {
      return
    }

    const timeoutId = window.setTimeout(() => setError(null), 3000)
    return () => window.clearTimeout(timeoutId)
  }, [errorMessage, setError])

  if (!errorMessage) return null

  return (
    <div
      className="error-toast"
      onClick={() => setError(null)}
      style={{ cursor: 'pointer' }}
    >
      {errorMessage}
    </div>
  )
}

function LoadingOverlay() {
  const { isLoading } = useGameStore()
  if (!isLoading) return null

  return (
    <div className="loading-spinner">
      <span>⟳</span>
      <span>Processing...</span>
    </div>
  )
}

export default function App() {
  useWebSocket()
  useAudio()

  const { room, member, screen } = useGameStore()
  const [shellMode, setShellMode] = useState<'desktop' | 'mobile'>(() => {
    const params = new URLSearchParams(window.location.search)
    const forced = params.get('shell')
    if (forced === 'mobile' || forced === 'desktop') {
      return forced
    }
    return window.innerWidth <= 768 ? 'mobile' : 'desktop'
  })
  const [isPortrait, setIsPortrait] = useState(() => window.innerHeight > window.innerWidth)

  useEffect(() => {
    const updateShellMode = () => {
      setIsPortrait(window.innerHeight > window.innerWidth)
      const params = new URLSearchParams(window.location.search)
      const forced = params.get('shell')
      if (forced === 'mobile' || forced === 'desktop') {
        setShellMode(forced)
        return
      }
      setShellMode(window.innerWidth <= 768 ? 'mobile' : 'desktop')
    }

    window.addEventListener('resize', updateShellMode)
    window.addEventListener('popstate', updateShellMode)
    updateShellMode()
    return () => {
      window.removeEventListener('resize', updateShellMode)
      window.removeEventListener('popstate', updateShellMode)
    }
  }, [])

  const renderContent = () => {
    if (screen === 'game' && room?.game) {
      return shellMode === 'mobile' ? <MobileGameShell /> : <GameShell />
    }
    if (screen === 'waiting' && room && !room.is_lobby) {
      return <WaitingRoom room={room} member={member} shellMode={shellMode} />
    }
    return <MenuScreen shellMode={shellMode} />
  }

  return (
    <div
      id="app"
      className={`app app-shell-${shellMode}`}
      style={{
        '--wallpaper-image': cssUrl('images', 'wallpaper.svg'),
        '--tabletop-image': cssUrl('images', 'tabletop3.jpg'),
        '--portrait-gate-image': cssUrl('images', 'tabletop3.jpg'),
      } as React.CSSProperties}
    >
      <div id="shell-selector" className={`shell-selector shell-selector-${shellMode}`}>
        <div className="desktop-shell-view">
          {shellMode === 'desktop' ? renderContent() : null}
        </div>
        <div id="mobile-shell" className={`shell mobile-shell${shellMode === 'mobile' ? '' : ' shell-hidden'}`}>
          <div className="mobile-shell-view">{shellMode === 'mobile' ? renderContent() : null}</div>
        </div>
        <div
          id="global-mobile-portrait-gate"
          className={`global-mobile-portrait-gate${shellMode === 'mobile' && isPortrait ? ' global-mobile-portrait-gate-visible' : ''}`}
        >
          <div className="mobile-portrait-gate-card">
            <div className="mobile-portrait-gate-card-inner">
              <img src={assetUrl('images', 'tabletop3.jpg')} alt="" className="mobile-portrait-gate-image" />
              <div className="mobile-portrait-gate-title">Colony Collapse</div>
              <div className="mobile-portrait-gate-body">Mobile play is landscape-only. Rotate your device or install the app.</div>
              <button type="button" id="mobile-portrait-install-pwa" className="mobile-portrait-gate-install-button">
                Install App
              </button>
              <div id="mobile-portrait-install-status" className="mobile-portrait-install-status" />
            </div>
          </div>
        </div>
      </div>

      {/* Global overlays */}
      {screen !== 'game' && <MenuAudioToggles />}
      <ErrorToast />
      <LoadingOverlay />
    </div>
  )
}
